import { useEffect, useState, Fragment, useCallback } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

const money = (n) => '$' + Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const iso = (d) => d.toISOString().slice(0, 10)

function presetRange(key) {
  const today = new Date()
  const to = iso(today)
  if (key === 'month') { const d = new Date(today.getFullYear(), today.getMonth(), 1); return { from: iso(d), to } }
  if (key === 'ytd')   { const d = new Date(today.getFullYear(), 0, 1);               return { from: iso(d), to } }
  if (key === 'all')   { return { from: '2020-01-01', to } }
  const d = new Date(today); d.setDate(d.getDate() - 90); return { from: iso(d), to } // 90d default
}

/**
 * Super-admin reseller SALES — revenue view over a date range with a money
 * breakdown that reconciles to gross (Gross = Vendor + InteGolf + Net + Tax).
 * Uses the same tested payout math as the Payouts page. Click a reseller to
 * drill into its individual bookings.
 */
export default function ResellerSales() {
  const [range, setRange] = useState(() => presetRange('90d'))
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [expanded, setExpanded] = useState(null)       // resellerAccountId
  const [detail, setDetail] = useState({})             // id -> { loading, bookings, err }

  const load = useCallback(() => {
    setLoading(true); setErr(null)
    tournamentApi.get('/admin/reseller-sales', { params: range })
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [range])
  useEffect(load, [load])

  const fetchDetail = useCallback((id) => {
    setDetail(d => ({ ...d, [id]: { ...(d[id] || {}), loading: true } }))
    tournamentApi.get(`/admin/reseller-sales/${id}`, { params: range })
      .then(r => setDetail(d => ({ ...d, [id]: { loading: false, bookings: r.data?.bookings || [] } })))
      .catch(e => setDetail(d => ({ ...d, [id]: { loading: false, err: e?.response?.data?.message || e.message } })))
  }, [range])

  function toggle(id) {
    if (expanded === id) { setExpanded(null); return }
    setExpanded(id)
    if (!detail[id]) fetchDetail(id)
  }
  // Re-fetch open drill-down when the range changes.
  useEffect(() => { setDetail({}); if (expanded != null) toggle(expanded) /* eslint-disable-next-line */ }, [range])

  const t = data?.totals
  const resellers = data?.resellers || []

  return (
    <AdminShell title="Reseller sales">
      <p style={{ color: '#64748b', marginTop: 0, marginBottom: 16, fontSize: 14, maxWidth: 780 }}>
        Sales across all resellers for the selected date range (confirmed / completed bookings, both payment modes).
        <strong> Gross</strong> reconciles to <strong>Vendor cost + InteGolf + Reseller net + Tax</strong>. Click a reseller to see its bookings.
      </p>

      {/* Date range + presets */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <label style={lbl}>From
          <input type="date" value={range.from} max={range.to} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} style={inp} />
        </label>
        <label style={lbl}>To
          <input type="date" value={range.to} min={range.from} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} style={inp} />
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['month', 'This month'], ['90d', 'Last 90 days'], ['ytd', 'This year'], ['all', 'All time']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setRange(presetRange(k))} style={presetBtn}>{label}</button>
          ))}
        </div>
      </div>

      {err && <div style={errorBox}>{err}</div>}

      <HotelCostBackfill onApplied={load} />

      {/* Grand-total cards */}
      {t && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Card label="Gross sales" value={money(t.gross)} big primary />
          <Card label="Vendor cost" value={money(t.vendorCost)} />
          <Card label="InteGolf revenue" value={money(t.integolf)} />
          <Card label="Reseller net" value={money(t.resellerNet)} />
          <Card label="Tax" value={money(t.tax)} />
          <Card label="Refunds" value={money(t.refunds)} />
        </div>
      )}

      {t && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: '#475569' }}>InteGolf fee status:</span>
          <span><span style={{ color: '#16a34a', fontWeight: 700 }}>●</span> collected</span>
          <span><span style={{ color: '#dc2626', fontWeight: 700 }}>●</span> not yet collected</span>
          {t.integolfOutstanding > 0 && (
            <span style={{ color: '#dc2626', fontWeight: 700 }}>⚠ {money(t.integolfOutstanding)} in InteGolf fees still to collect from resellers</span>
          )}
        </div>
      )}

      {loading ? <div style={empty}>Loading…</div> : resellers.length === 0 ? (
        <div style={empty}>No sales in this range.</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflowX: 'auto' }}>
          <table style={tbl}>
            <thead>
              <tr style={tr}>
                <th style={th}>Reseller</th>
                <th style={thR}>Bookings</th>
                <th style={thR}>Gross</th>
                <th style={thR}>Vendor cost</th>
                <th style={thR}>InteGolf</th>
                <th style={thR}>Reseller net</th>
                <th style={thR}>Tax</th>
                <th style={thR}>Refunds</th>
              </tr>
            </thead>
            <tbody>
              {resellers.map(r => (
                <Fragment key={r.resellerAccountId}>
                  <tr style={{ ...tr, cursor: 'pointer', background: expanded === r.resellerAccountId ? '#f8fafc' : '#fff' }} onClick={() => toggle(r.resellerAccountId)}>
                    <td style={td}><strong>{expanded === r.resellerAccountId ? '▾' : '▸'} {r.name}</strong> <span style={{ color: '#94a3b8', fontSize: 12 }}>{r.currency}</span></td>
                    <td style={tdR}>{r.bookingCount}</td>
                    <td style={{ ...tdR, fontWeight: 700 }}>{money(r.gross)}</td>
                    <td style={tdR}>{money(r.vendorCost)}</td>
                    <td style={{ ...tdR, color: '#0f766e', fontWeight: 600 }}
                        title={r.integolfOutstanding > 0 ? `${money(r.integolfOutstanding)} of InteGolf fees not yet collected from this reseller` : 'All InteGolf fees collected'}>
                      {money(r.integolf)}
                      {r.integolfOutstanding > 0 && <span style={{ display: 'block', fontSize: 10.5, color: '#dc2626', fontWeight: 700 }}>● {money(r.integolfOutstanding)} owed</span>}
                    </td>
                    <td style={tdR}>{money(r.resellerNet)}</td>
                    <td style={tdR}>{money(r.tax)}</td>
                    <td style={{ ...tdR, color: r.refunds > 0 ? '#b91c1c' : '#94a3b8' }}>{money(r.refunds)}</td>
                  </tr>
                  {expanded === r.resellerAccountId && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, background: '#f8fafc' }}>
                        <DrillDown state={detail[r.resellerAccountId]} onRefunded={() => { fetchDetail(r.resellerAccountId); load() }} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  )
}

// Maintenance: recover hotel Cost/Markup left null by an empty client price at
// booking time (reads the real amount from the stored Expedia response). Shows
// only when there's something to fix. Preview list + one-click Apply.
function HotelCostBackfill({ onApplied }) {
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState(null)

  const load = useCallback(() => {
    tournamentApi.get('/admin/reseller-sales/hotel-cost-backfill/preview')
      .then(r => setPreview(r.data)).catch(() => setPreview(null))
  }, [])
  useEffect(load, [load])

  async function apply() {
    if (!window.confirm(`Recover hotel cost on ${preview.count} booking${preview.count === 1 ? '' : 's'} (${money(preview.totalRecovered)})? This writes to those bookings.`)) return
    setBusy(true)
    try {
      const r = await tournamentApi.post('/admin/reseller-sales/hotel-cost-backfill/apply')
      setResult(r.data); load(); onApplied && onApplied()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  if (result && (!preview || preview.count === 0)) {
    return <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#166534' }}>
      ✓ Recovered hotel cost on {result.count} booking{result.count === 1 ? '' : 's'} ({money(result.totalRecovered)}).
    </div>
  }
  if (!preview || preview.count === 0) return null

  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>
            ⚠ {preview.count} booking{preview.count === 1 ? '' : 's'} missing hotel cost — {money(preview.totalRecovered)} recoverable
          </div>
          <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
            The hotel cost wasn't recorded at booking; it's recovered from the stored Expedia response (also fixes vendor cost + reseller net + Mode-B recovery).
            {preview.settledAffected > 0 && <> <strong>{preview.settledAffected} already paid out</strong> — those also need a payout re-check.</>}
            {preview.unrecoverable > 0 && <> {preview.unrecoverable} can't be auto-recovered.</>}
          </div>
        </div>
        <button type="button" onClick={() => setOpen(o => !o)} style={presetBtn}>{open ? 'Hide' : 'Preview'}</button>
        <button type="button" onClick={apply} disabled={busy} style={{ padding: '9px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Recovering…' : 'Recover now'}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 10, background: '#fff', border: '1px solid #fde68a', borderRadius: 6, overflowX: 'auto' }}>
          <table style={{ ...tbl, fontSize: 13 }}>
            <thead><tr style={tr}>
              <th style={thSub}>Booking</th><th style={thSub}>Reseller</th><th style={thSub}>Customer</th><th style={thSub}>Status</th><th style={thSub}>Mode</th>
              <th style={thSubR}>Hotel amount</th><th style={thSubR}>Cost</th><th style={thSubR}>Markup</th>
            </tr></thead>
            <tbody>
              {preview.items.map(i => (
                <tr key={i.bookingId} style={tr}>
                  <td style={tdSub}>#{i.bookingId}</td>
                  <td style={tdSub}>{i.reseller}</td>
                  <td style={tdSub}>{i.customer}</td>
                  <td style={tdSub}>{i.status}{i.settled ? ' · paid out' : ''}</td>
                  <td style={tdSub}>{i.paymentMode}</td>
                  <td style={tdSubR}>{money(i.amount)}</td>
                  <td style={tdSubR}>{money(i.cost)}</td>
                  <td style={tdSubR}>{money(i.markup)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function DrillDown({ state, onRefunded }) {
  const [refundFor, setRefundFor] = useState(null)   // booking object being refunded
  if (!state || state.loading) return <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>Loading bookings…</div>
  if (state.err) return <div style={{ padding: 16, color: '#b91c1c', fontSize: 13 }}>{state.err}</div>
  const bookings = state.bookings || []
  if (bookings.length === 0) return <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>No bookings.</div>
  return (
    <>
      <table style={{ ...tbl, fontSize: 13 }}>
        <thead>
          <tr style={tr}>
            <th style={thSub}>#</th><th style={thSub}>Date</th><th style={thSub}>Customer</th><th style={thSub}>Item</th>
            <th style={thSub}>Status</th><th style={thSub}>Mode</th>
            <th style={thSubR}>Gross</th><th style={thSubR}>Vendor</th><th style={thSubR}>InteGolf</th>
            <th style={thSubR}>Net</th><th style={thSubR}>Tax</th><th style={thSubR}>Refund</th>
            <th style={thSubR}></th>
          </tr>
        </thead>
        <tbody>
          {bookings.map(b => (
            <tr key={b.bookingId} style={tr}>
              <td style={tdSub}>{b.bookingId}</td>
              <td style={tdSub}>{String(b.saleDate || '').slice(0, 10)}</td>
              <td style={tdSub}>{b.customer}</td>
              <td style={tdSub}>{b.label}{b.grouping ? <span style={{ color: '#94a3b8' }}> · {b.grouping}</span> : null}</td>
              <td style={tdSub}>{b.status}</td>
              <td style={tdSub}>{b.paymentMode}</td>
              <td style={{ ...tdSubR, fontWeight: 600 }}>{money(b.gross)}</td>
              <td style={tdSubR}>{money(b.vendorCost)}</td>
              <td style={{ ...tdSubR, color: b.integolfCharged ? '#16a34a' : '#dc2626', fontWeight: 600 }}
                  title={b.integolfCharged
                    ? (b.paymentMode === 'A' ? 'InteGolf fee collected (taken from the customer payment)' : 'InteGolf fee collected from the reseller')
                    : `InteGolf fee NOT collected yet — ${money(b.integolfOutstanding)} outstanding on the reseller's card`}>
                {money(b.integolf)}{!b.integolfCharged && <span style={{ marginLeft: 3 }}>●</span>}
              </td>
              <td style={tdSubR}>{money(b.resellerNet)}</td>
              <td style={tdSubR}>{money(b.tax)}</td>
              <td style={{ ...tdSubR, color: b.refund > 0 ? '#b91c1c' : '#cbd5e1' }}>{money(b.refund)}</td>
              <td style={{ ...tdSubR, whiteSpace: 'nowrap' }}>
                {b.paymentMode === 'A'
                  ? <button type="button" onClick={() => setRefundFor(b)} style={refundBtn}>Refund</button>
                  : <span title="Mode B refunds go through the reseller's own gateway" style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {refundFor && (
        <RefundModal
          booking={refundFor}
          onClose={() => setRefundFor(null)}
          onDone={() => { setRefundFor(null); onRefunded && onRefunded() }}
        />
      )}
    </>
  )
}

/**
 * Super-admin refund modal (Mode A only). Pick 0–100 %, optionally cancel the
 * tee time / hotel at the provider, and type "refund" to confirm. POSTs to
 * /admin/reseller-sales/refund and reports the gateway result.
 */
function RefundModal({ booking, onClose, onDone }) {
  const [percent, setPercent] = useState(100)
  const [cancelProviders, setCancelProviders] = useState(true)
  const [reason, setReason] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [result, setResult] = useState(null)

  const gross = Number(booking.gross || 0)
  const pct = Math.max(0, Math.min(100, Number(percent) || 0))
  const estRefund = Math.round(gross * pct) / 100
  const canSubmit = confirm.trim().toLowerCase() === 'refund' && (pct > 0 || cancelProviders) && !busy

  async function submit() {
    setBusy(true); setErr(null)
    try {
      const { data } = await tournamentApi.post('/admin/reseller-sales/refund', {
        bookingId: booking.bookingId,
        refundPercent: pct,
        cancelProviders,
        reason: reason.trim() || null,
        confirm: confirm.trim().toLowerCase(),
      })
      setResult(data)
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally { setBusy(false) }
  }

  return (
    <div style={modalOverlay} onClick={busy ? undefined : onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
          Refund booking #{booking.bookingId}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
          {booking.customer} · {booking.label} · gross {money(gross)}
        </div>

        {result ? (
          <>
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 14, fontSize: 13, color: '#166534' }}>
              ✓ {result.cancelProviders ? 'Cancelled at provider and refunded' : 'Refunded'} {money(result.refundedAmount)} ({result.refundPercent}%).
              New status: <strong>{result.newStatus}</strong>.
              {Array.isArray(result.opsNotes) && result.opsNotes.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#92400e' }}>
                  {result.opsNotes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" onClick={onDone} style={modalPrimaryBtn}>Done</button>
            </div>
          </>
        ) : (
          <>
            <label style={{ ...lbl, marginBottom: 14 }}>Refund amount
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                <input type="range" min={0} max={100} step={1} value={percent}
                  onChange={e => setPercent(Number(e.target.value))} style={{ flex: 1 }} />
                <input type="number" min={0} max={100} value={percent}
                  onChange={e => setPercent(Number(e.target.value))}
                  style={{ ...inp, width: 70, textAlign: 'right' }} />
                <span style={{ fontSize: 13, color: '#475569' }}>%</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#0f172a' }}>
                ≈ <strong>{money(estRefund)}</strong> back to the customer's card
              </div>
            </label>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={cancelProviders} onChange={e => setCancelProviders(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ fontSize: 13, color: '#0f172a' }}>
                <strong>Also cancel at the provider</strong> — release the tee time(s) and hotel booking.
                <span style={{ display: 'block', color: '#64748b', marginTop: 2 }}>
                  Unchecked = money-only refund; the inventory stays booked.
                </span>
              </span>
            </label>

            <label style={{ ...lbl, marginBottom: 14 }}>Reason <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional, internal)</span>
              <input type="text" value={reason} onChange={e => setReason(e.target.value)}
                placeholder="e.g. customer request, course closure" style={{ ...inp, marginTop: 6 }} />
            </label>

            <label style={{ ...lbl, marginBottom: 8 }}>Type <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>refund</code> to confirm
              <input type="text" value={confirm} onChange={e => setConfirm(e.target.value)}
                autoComplete="off" placeholder="refund" style={{ ...inp, marginTop: 6 }} />
            </label>

            {err && <div style={{ ...errorBox, marginTop: 10, marginBottom: 0 }}>{err}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button type="button" onClick={onClose} disabled={busy} style={modalGhostBtn}>Cancel</button>
              <button type="button" onClick={submit} disabled={!canSubmit}
                style={{ ...modalPrimaryBtn, background: canSubmit ? '#b91c1c' : '#fca5a5', cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
                {busy ? 'Processing…' : (cancelProviders ? `Refund ${money(estRefund)} + cancel` : `Refund ${money(estRefund)}`)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Card({ label, value, big, primary }) {
  return (
    <div style={{ background: primary ? '#0f172a' : '#fff', border: '1px solid ' + (primary ? '#0f172a' : '#e2e8f0'), borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: primary ? '#94a3b8' : '#64748b' }}>{label}</div>
      <div style={{ fontSize: big ? 24 : 20, fontWeight: 800, marginTop: 4, color: primary ? '#fff' : '#0f172a' }}>{value}</div>
    </div>
  )
}

const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const thR = { ...th, textAlign: 'right' }
const td = { padding: '12px 16px', verticalAlign: 'middle' }
const tdR = { ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }
const thSub = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#94a3b8', fontSize: 11 }
const thSubR = { ...thSub, textAlign: 'right' }
const tdSub = { padding: '7px 12px' }
const tdSubR = { ...tdSub, textAlign: 'right', fontFamily: 'monospace' }
const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' }
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }
const presetBtn = { padding: '8px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const refundBtn = { padding: '4px 10px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }
const modalPrimaryBtn = { padding: '9px 18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }
const modalGhostBtn = { padding: '9px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
